import { useCallback, useRef } from 'react';
import { useDefaultStyleContext } from '../../../../context/complexDefaultStyleContext';
import { useDocumentContext } from '../../../../context/complexDocumentContext';

import { sanitizeFontSize } from '../../../../utils/helper';

export const useFontSettings = (CurrentDesign) => {
  const getDefaultStyleSelector = useCallback(store => ({fontFamily: store.fontFamily, fontSize:store.fontSize}), []);
  const [defaultStyle] = useDefaultStyleContext(getDefaultStyleSelector);
  const [, methods] = useDocumentContext(()=>null, 'useFontSettings');
  const inputRef = useRef();

  const onSizeChange = useCallback(newFontSize => {
    if (!isNaN(newFontSize)) {
      methods.updatefontFamilyAndSize(CurrentDesign, newFontSize, defaultStyle.fontSize, 'fontSize', 'update' );
    }
  }, [CurrentDesign, defaultStyle.fontSize, methods]);

  const changeFontFamily = useCallback(newFontFamily => {
    methods.updatefontFamilyAndSize(CurrentDesign, newFontFamily, defaultStyle.fontFamily, 'fontFamily', 'update' );
  }, [CurrentDesign, defaultStyle.fontFamily, methods]);

  const handleKeypress = useCallback(e => {
    e.stopPropagation();
    if (['Enter', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
      e.preventDefault();
      const value = parseFloat(inputRef.current.innerHTML.trim());
      if (!isNaN(value) && value >= 1 && value <= 400) {
        onSizeChange(value);
      }
    }
  }, [onSizeChange]);

  const handleKeyDown = e => {
    e.stopPropagation();

    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
      'Enter'
    ];

    if (allowedKeys.includes(e.key)) return;

    if (!/^[0-9.]$/.test(e.key)) {
      e.preventDefault();
      return;
    }

    const el = inputRef.current;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const current = el.innerText || '';

    // caret offsets
    const start = range.startOffset;
    const end = range.endOffset;

    // 🔑 build next value using caret position
    const next =
      current.slice(0, start) +
      e.key +
      current.slice(end);

    const sanitized = sanitizeFontSize(next);

    e.preventDefault();

    if (!sanitized) return;

    el.innerText = sanitized;

    // 🔑 restore caret near where user typed
    requestAnimationFrame(() => {
      const newRange = document.createRange();
      const sel = window.getSelection();

      const caretPos = Math.min(
        start + 1,
        sanitized.length
      );

      newRange.setStart(el.firstChild || el, caretPos);
      newRange.collapse(true);

      sel.removeAllRanges();
      sel.addRange(newRange);
    });
  };

  const handlePaste = e => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    const sanitized = sanitizeFontSize(pasted);
    inputRef.current.innerText = sanitized;

    requestAnimationFrame(() => {
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(inputRef.current);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    });
  };

  return {
    fontFamily: defaultStyle.fontFamily,
    fontSize: defaultStyle.fontSize,
    onSizeChange,
    changeFontFamily,
    handleKeypress,
    handleKeyDown,
    handlePaste,
    inputRef,
  };
};


